import React, { MutableRefObject } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import ButtonBase from '@mui/material/ButtonBase'
import { RootActionType, RootState } from 'duck'
import { ReduxProps } from 'utils/Types'
import { RenderItemProps } from 'components/ListVirtualized'
import FixedSizeList from 'components/ListVirtualized/FixedSizeList'
import GridImage from './GridImage'
import { actions, selectors, STEP } from 'containers/NFTSellPanel/duck'
import { UserImage } from 'models/ApiModels'
import { useBreakpoint } from 'utils/Hooks'
import stylesGlobal from 'stylesGlobal.module.scss'
import { GRID_PROJECTS_CONFIG } from '.'
import Button from 'components/Button'
import styles from '../../NFTSellForm.module.scss'
import { values } from 'appConstants'
import Typography from 'components/Typography'

const mapStateToProps = (state: RootState) => ({
  appliedSelectedGridCount: selectors.appliedSelectedGridCount(state),
  gridLoading: selectors.gridLoading(state),
  gridCount: selectors.gridCount(state),
  isAppliedSelectedImageEnough: selectors.isAppliedSelectedImageEnough(state),
  selectedGridImages: selectors.selectedGridImages(state),
  thumbnail: selectors.formData(state).thumbnail
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      updateFormData: actions.updateFormData,
      setStep: actions.setStep
    },
    dispatch
  )

type ImagesListProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps> & {
  dialogBodyRef?: MutableRefObject<any>
}

const ImagesList: React.FC<ImagesListProps> = props => {
  const {
    updateFormData,
    setStep,
    gridCount = 0,
    dialogBodyRef,
    gridLoading,
    isAppliedSelectedImageEnough,
    thumbnail,
    selectedGridImages,
    appliedSelectedGridCount
  } = props

  const { isSmallscreenAbove } = useBreakpoint()

  const isMostImageAlreadyPublished =
    gridCount >= values.MINIMUM_SELL_NFT_IMAGE &&
    appliedSelectedGridCount < values.MINIMUM_SELL_NFT_IMAGE

  return (
    <>
      <div className={clsx('w-full', styles.ContainerImagesList)}>
        {isSmallscreenAbove ? (
          <div className={clsx('flex items-center', stylesGlobal.BorderBottomOnSurface)}>
            {isAppliedSelectedImageEnough ? (
              <Typography
                className="ml-auto mr-3 text-white-med-on-dark"
                variant="body2"
                component="div"
              >{`${appliedSelectedGridCount} / ${gridCount} images will be in your collection`}</Typography>
            ) : (
              <Typography
                className="ml-auto mr-3 text-danger"
                variant="body2"
                component="div"
              >{`Select at least ${values.MINIMUM_SELL_NFT_IMAGE} images`}</Typography>
            )}

            <Button
              className={isSmallscreenAbove ? '-mr-2.5' : '-ml-2.5'}
              color="secondary"
              onClick={() => setStep(STEP.CURATE_COLLECTION)}
              size="small"
            >
              EDIT COLLECTION
            </Button>
          </div>
        ) : null}

        {!isSmallscreenAbove ? (
          <Button
            className="-ml-2.5"
            color="secondary"
            onClick={() => setStep(STEP.CURATE_COLLECTION)}
            size="small"
          >
            EDIT COLLECTION
          </Button>
        ) : null}
      </div>

      <div className="h-1/100 min-h-0 flex-auto">
        {isMostImageAlreadyPublished ? (
          <div className={styles.EmptyCollectionView}>
            <Typography variant="subtitle1">
              <i>
                {appliedSelectedGridCount > 0
                  ? "You don't have enough unpublished images in this project. Save or generate more images to continue."
                  : 'All of your images in this project already published.  Save or generate more images to continue.'}
              </i>
            </Typography>
          </div>
        ) : (
          <FixedSizeList<UserImage>
            autoHeight={!isSmallscreenAbove}
            scrollRef={!isSmallscreenAbove ? dialogBodyRef : undefined}
            dataLength={selectedGridImages.length}
            loading={Boolean(gridLoading)}
            config={GRID_PROJECTS_CONFIG}
            renderItem={(props: RenderItemProps<UserImage>) => {
              const { height, index, width } = props

              if (!selectedGridImages[index]) return null

              const item = selectedGridImages[index]

              const selected = thumbnail === item.id

              return (
                <div className="h-full w-full select-none">
                  <ButtonBase
                    disabled={item.is_published}
                    className="h-full w-full"
                    onClick={() => updateFormData({ thumbnail: item.id })}
                  >
                    <GridImage
                      component="span"
                      containerWidth={width}
                      containerHeight={height}
                      selected={selected}
                      image={item}
                    />
                  </ButtonBase>
                </div>
              )
            }}
          />
        )}
      </div>
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ImagesList)
